import { useEffect, useState } from "react";
import { Trophy, Crown, Coins, TrendingUp, Award } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { formatCoins, avatarUrl } from "../lib/format";

const medal = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function Leaderboard() {
  const { t } = useLang();
  const [tab, setTab] = useState("all");
  const [all, setAll] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [prize, setPrize] = useState(null);

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setAll(data)).catch(() => {});
    api.get("/leaderboard/weekly").then(({ data }) => setWeekly(data)).catch(() => {});
    api.get("/daily-prize/latest").then(({ data }) => setPrize(data)).catch(() => {});
  }, []);

  const rows = tab === "all" ? all : weekly;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("leaderboard")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("topPlayers")}</p>
      </div>

      {prize?.winners?.length > 0 && (
        <div className="mw-card p-5 relative overflow-hidden" data-testid="daily-prize-card"
          style={{ background: "radial-gradient(circle at 10% 0%, rgba(255,215,0,0.15), transparent 60%)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Award size={20} className="text-amber-300" />
            <h2 className="font-display text-lg font-bold gold-text">{t("dailyWinners")}</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {prize.winners.map((w, i) => (
              <div key={i} className="rounded-xl border border-amber-500/25 bg-black/30 p-3 flex items-center gap-3" data-testid={`daily-winner-${i}`}>
                <Crown size={20} style={{ color: medal[i] }} />
                <div className="min-w-0">
                  <p className="font-semibold truncate">{w.name}</p>
                  <p className="text-xs font-mono-num text-emerald-400">+{formatCoins(w.prize)} {t("coins")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="inline-flex rounded-xl border border-amber-500/25 bg-black/30 p-1">
        {[
          { id: "all", label: t("allTime") },
          { id: "weekly", label: t("weekly") },
        ].map((x) => (
          <button
            key={x.id}
            onClick={() => setTab(x.id)}
            data-testid={`leaderboard-tab-${x.id}`}
            className={`rounded-lg px-5 py-2 text-sm font-bold transition-colors ${tab === x.id ? "btn-gold" : "text-slate-300 hover:text-amber-200"}`}
          >
            {x.label}
          </button>
        ))}
      </div>

      <div className="mw-card overflow-hidden" data-testid="leaderboard-list">
        {rows.map((r, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 ${i < 3 ? "bg-amber-500/5" : ""}`}
            data-testid={`leaderboard-row-${i}`}
          >
            <div className="w-8 text-center">
              {i < 3 ? (
                <Crown size={20} style={{ color: medal[i] }} className="mx-auto" />
              ) : (
                <span className="font-mono-num text-slate-500 font-bold">{i + 1}</span>
              )}
            </div>
            <div className="grid place-items-center h-9 w-9 rounded-full bg-gradient-to-br from-amber-400/30 to-purple-600/20 border border-white/10 font-display font-bold overflow-hidden">
              {r.picture ? <img src={avatarUrl(r.picture)} alt="" className="h-full w-full object-cover" /> : (r.name?.[0] || "?").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-semibold truncate block">{r.name}</span>
              {tab === "weekly" && (
                <span className="text-[11px] text-slate-500">{r.rounds} {t("rounds")}</span>
              )}
            </div>
            {tab === "all" ? (
              <span className="flex items-center gap-1 font-mono-num font-bold text-amber-200">
                <Coins size={14} className="text-amber-400" /> {formatCoins(r.coins)}
              </span>
            ) : (
              <span className={`flex items-center gap-1 font-mono-num font-bold ${r.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                <TrendingUp size={14} /> {r.net >= 0 ? "+" : ""}{formatCoins(r.net)}
              </span>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="p-10 text-center text-slate-500">
            <Trophy size={40} className="mx-auto mb-2 opacity-40" /> —
          </div>
        )}
      </div>
    </div>
  );
}
